import { disableBodyScroll } from 'body-scroll-lock';
import React, { useEffect, useRef } from 'react';

import ModalFrame from './ModalFrame';
import type { ModalFrameProps } from './types';

type StandardModalBackdrop = 'clear' | 'dim' | 'fade-in' | 'fade-out';

interface StandardModalProps extends ModalFrameProps {
  backdrop: StandardModalBackdrop;
  isFullScreen?: boolean;
  preventBodyScrollLock: boolean;
}

const BACKDROP_CLASSES: Record<StandardModalBackdrop, string> = {
  clear: 'bg-transparent',
  dim: 'bg-black/60',
  'fade-in': 'bg-transparent animate-backdrop-fade-in',
  'fade-out': 'bg-transparent animate-backdrop-fade-out'
};

export default function StandardModal({
  backdrop,
  background,
  children,
  closeButtonMotion,
  disableClickOutside,
  hideClose,
  isFullBody,
  isFullScreen,
  isFullWidth,
  onCloseClick,
  preventBodyScrollLock
}: StandardModalProps) {
  const targetRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null);

  useEffect(() => {
    if (!preventBodyScrollLock && targetRef.current) {
      disableBodyScroll(targetRef.current);
    }
  }, [targetRef, preventBodyScrollLock]);

  return (
    <div
      data-testid="standard-modal"
      ref={targetRef}
      className={`fixed left-0 size-full overflow-auto transition-colors duration-300 ease-in-out ${
        isFullScreen ? 'top-0 z-50' : 'top-[50px] z-[9999] lg:top-[70px]'
      } ${BACKDROP_CLASSES[backdrop]}`}
    >
      <div className="relative mx-auto size-full max-w-full overflow-y-auto">
        <div className="flex min-h-screen w-full flex-col items-center">
          <ModalFrame
            onCloseClick={onCloseClick}
            closeButtonMotion={closeButtonMotion}
            disableClickOutside={disableClickOutside}
            hideClose={hideClose}
            isFullBody={isFullBody}
            background={background}
            isFullWidth={isFullWidth}
          >
            {children}
          </ModalFrame>
        </div>
      </div>
    </div>
  );
}
